/**
 * Main Team Chat application page.
 * Provides real-time team communication with channels, DMs, and groups.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Avatar,
  Checkbox,
  Button,
  Stack,
  CircularProgress,
} from '@mui/joy';
import toast from 'react-hot-toast';
import {
  ChatLayout,
  ChatHeader,
  MessageList,
  MessageComposer,
  ChannelSearchModal,
  ThreadView,
} from '../components/chat';
import {
  useChannel,
  useChannelMessages,
  useChannelMembers,
  useDirectMessages,
  useSendMessage,
  useUpdateMessage,
  useDeleteMessage,
  useAddReaction,
  useRemoveReaction,
  useMarkChannelRead,
  useLeaveChannel,
  useInviteToChannel,
} from '../hooks/useChat';
import { useUsers } from '../hooks/useCrm';
import { useChatWebSocket, useChannelTyping } from '../hooks/useChatWebSocket';
import { useAuth } from '../contexts/AuthContext';
import type { MessageCreatePayload, ChatUser } from '../types/chat';

// LocalStorage key for muted channels
const MUTED_CHANNELS_KEY = 'chat_muted_channels';

// Get muted channel IDs from localStorage
const getMutedChannels = (): Set<string> => {
  try {
    const stored = localStorage.getItem(MUTED_CHANNELS_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
};

// Save muted channel IDs to localStorage
const saveMutedChannels = (mutedChannels: Set<string>): void => {
  localStorage.setItem(MUTED_CHANNELS_KEY, JSON.stringify([...mutedChannels]));
};

const ChatApp: React.FC = () => {
  const { channelId: urlChannelId, userId: urlUserId } = useParams<{
    channelId?: string;
    userId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [activeChannelId, setActiveChannelId] = useState<string | null>(
    urlChannelId || null
  );
  const [replyToMessageId, setReplyToMessageId] = useState<string | null>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [mutedChannels, setMutedChannels] = useState<Set<string>>(() => getMutedChannels());
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedInviteIds, setSelectedInviteIds] = useState<Set<string>>(new Set());

  const { data: directMessagesData } = useDirectMessages();
  const directMessages = Array.isArray(directMessagesData) ? directMessagesData : [];

  // Toggle mute for a channel
  const handleMuteChannel = useCallback((channelId: string, muted: boolean) => {
    setMutedChannels((prev) => {
      const newSet = new Set(prev);
      if (muted) {
        newSet.add(channelId);
      } else {
        newSet.delete(channelId);
      }
      saveMutedChannels(newSet);
      return newSet;
    });
  }, []);

  // Update active channel from URL
  useEffect(() => {
    if (urlChannelId) {
      setActiveChannelId(urlChannelId);
    }
  }, [urlChannelId]);

  // When on /chat/dm/:userId, resolve to channel and use channel messages (same as mobile)
  useEffect(() => {
    if (!urlUserId || urlChannelId) return;
    const dm = directMessages.find(
      (d) => String(d.other_user?.id) === String(urlUserId)
    );
    if (dm?.channel?.id) {
      setActiveChannelId(dm.channel.id);
      navigate(`/chat/channels/${dm.channel.id}`, { replace: true });
    }
  }, [urlUserId, urlChannelId, directMessages, navigate]);

  // Queries
  const { data: channel, isLoading: channelLoading } = useChannel(activeChannelId);
  const {
    data: messagesData,
    isLoading: messagesLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useChannelMessages(activeChannelId);

  // Extract flattened messages from infinite query
  const messages = messagesData?.messages || [];

  // For DM channels, get the other user from members
  const getOtherUserForDM = (): ChatUser | null => {
    if (!channel || channel.channel_type !== 'direct' || !channel.members) {
      return null;
    }
    const otherMember = channel.members.find(
      (member) => String(member.user.id) !== String(user?.id)
    );
    return otherMember?.user || null;
  };

  const otherUser = getOtherUserForDM();

  // Mutations
  const sendMessage = useSendMessage();
  const updateMessage = useUpdateMessage();
  const deleteMessage = useDeleteMessage();
  const addReaction = useAddReaction();
  const removeReaction = useRemoveReaction();
  const markRead = useMarkChannelRead();
  const leaveChannelMutation = useLeaveChannel();
  const inviteToChannel = useInviteToChannel();

  // Members (lazy-loaded only when the members modal is open)
  const { data: channelMembers = [], isLoading: membersLoading } = useChannelMembers(
    membersModalOpen ? activeChannelId : null
  );

  // Org users for the invite picker (lazy-loaded only when the invite modal is open)
  const { data: orgUsers = [], isLoading: usersLoading } = useUsers();

  // Users not already members of the current channel
  const invitableUsers = useMemo(() => {
    const memberUserIds = new Set(
      (channel?.members || []).map((m) => String(m.user.id))
    );
    return orgUsers.filter((u) => !memberUserIds.has(String(u.id)));
  }, [orgUsers, channel]);

  // WebSocket connection
  const {
    isConnected,
    joinChannel,
    leaveChannel,
    startTyping,
    stopTyping,
    markRead: wsMarkRead,
    typingUsers,
    onlineUsers,
  } = useChatWebSocket({
    autoConnect: true,
    currentUserId: user?.id,
    activeChannelId,
    mutedChannelIds: mutedChannels,
    onMessage: (message) => {
      // Message updates handled by React Query cache invalidation
    },
  });

  // Get typing users for current channel
  const currentTypingUsers = useChannelTyping(activeChannelId, typingUsers);

  // Join/leave channel when it changes
  useEffect(() => {
    if (activeChannelId && isConnected) {
      joinChannel(activeChannelId);

      // Mark as read when viewing
      markRead.mutate({ channelId: activeChannelId });

      return () => {
        leaveChannel(activeChannelId);
      };
    }
  }, [activeChannelId, isConnected, joinChannel, leaveChannel]);

  // Handlers
  const handleChannelSelect = useCallback(
    (channelId: string) => {
      setActiveChannelId(channelId);
      setReplyToMessageId(null);
      navigate(`/chat/channels/${channelId}`);
    },
    [navigate]
  );

  const handleDMSelect = useCallback(
    (userId: string) => {
      // Navigate to DM (will use the existing channel or create one)
      navigate(`/chat/dm/${userId}`);
    },
    [navigate]
  );

  const handleSendMessage = useCallback(
    async (data: MessageCreatePayload) => {
      if (!activeChannelId) return;

      try {
        await sendMessage.mutateAsync({
          channelId: activeChannelId,
          data: {
            ...data,
            parent_message_id: replyToMessageId,
          },
        });
        setReplyToMessageId(null);
        stopTyping(activeChannelId);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    },
    [activeChannelId, replyToMessageId, sendMessage, stopTyping]
  );

  const handleTyping = useCallback(() => {
    if (activeChannelId) {
      startTyping(activeChannelId);
    }
  }, [activeChannelId, startTyping]);

  const handleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!activeChannelId) return;

      try {
        await addReaction.mutateAsync({
          messageId,
          emoji,
          channelId: activeChannelId,
        });
      } catch (error) {
        console.error('Failed to add reaction:', error);
      }
    },
    [activeChannelId, addReaction]
  );

  const handleRemoveReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!activeChannelId) return;

      try {
        await removeReaction.mutateAsync({
          messageId,
          emoji,
          channelId: activeChannelId,
        });
      } catch (error) {
        console.error('Failed to remove reaction:', error);
      }
    },
    [activeChannelId, removeReaction]
  );

  const handleEditMessage = useCallback(
    async (messageId: string, content: string) => {
      if (!activeChannelId) return;

      // TODO: Implement edit modal
      const newContent = prompt('Edit message:', content);
      if (newContent && newContent !== content) {
        try {
          await updateMessage.mutateAsync({
            channelId: activeChannelId,
            messageId,
            data: { content: newContent },
          });
        } catch (error) {
          console.error('Failed to edit message:', error);
        }
      }
    },
    [activeChannelId, updateMessage]
  );

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      if (!activeChannelId) return;

      if (window.confirm('Are you sure you want to delete this message?')) {
        try {
          await deleteMessage.mutateAsync({
            channelId: activeChannelId,
            messageId,
          });
        } catch (error) {
          console.error('Failed to delete message:', error);
        }
      }
    },
    [activeChannelId, deleteMessage]
  );

  const handleReply = useCallback((messageId: string) => {
    // Open the thread view drawer
    setOpenThreadId(messageId);
  }, []);

  const handleCloseThread = useCallback(() => {
    setOpenThreadId(null);
  }, []);

  const handleLeaveChannel = useCallback(async () => {
    if (!activeChannelId) return;
    if (!window.confirm('Are you sure you want to leave this channel?')) return;
    try {
      await leaveChannelMutation.mutateAsync(activeChannelId);
      toast.success('You left the channel');
      setActiveChannelId(null);
      navigate('/chat');
    } catch (error) {
      console.error('Failed to leave channel:', error);
      toast.error('Failed to leave channel');
    }
  }, [activeChannelId, leaveChannelMutation, navigate]);

  const handleToggleInvite = useCallback((userId: string) => {
    setSelectedInviteIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleSubmitInvite = useCallback(async () => {
    if (!activeChannelId || selectedInviteIds.size === 0) return;
    try {
      await inviteToChannel.mutateAsync({
        channelId: activeChannelId,
        data: { user_ids: Array.from(selectedInviteIds) },
      });
      toast.success(
        selectedInviteIds.size === 1
          ? '1 person invited to the channel'
          : `${selectedInviteIds.size} people invited to the channel`
      );
      setSelectedInviteIds(new Set());
      setInviteModalOpen(false);
    } catch (error) {
      console.error('Failed to invite users:', error);
      toast.error('Failed to invite people');
    }
  }, [activeChannelId, selectedInviteIds, inviteToChannel]);

  // Render empty state when no channel selected
  const renderEmptyState = () => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        textAlign: 'center',
      }}
    >
      <Typography level="h3" sx={{ mb: 2 }}>
        Welcome to Team Chat
      </Typography>
      <Typography level="body-md" color="neutral">
        Select a channel from the sidebar to start chatting with your team.
      </Typography>
    </Box>
  );

  // Render chat content
  const renderChatContent = () => {
    if (!activeChannelId) {
      return renderEmptyState();
    }

    return (
      <>
        <ChatHeader
          channel={channel || null}
          isLoading={channelLoading}
          otherUser={otherUser}
          onShowInfo={() => {
            // No dedicated channel info panel exists yet; the InfoIcon is left as-is.
          }}
          onShowMembers={() => {
            setMembersModalOpen(true);
          }}
          onInvite={() => {
            setSelectedInviteIds(new Set());
            setInviteModalOpen(true);
          }}
          onSearch={() => {
            setSearchModalOpen(true);
          }}
          onLeave={handleLeaveChannel}
          onMute={(muted) => {
            if (activeChannelId) {
              handleMuteChannel(activeChannelId, muted);
            }
          }}
          isMuted={activeChannelId ? mutedChannels.has(activeChannelId) : false}
        />

        <MessageList
          messages={messages}
          channelId={activeChannelId}
          isLoading={messagesLoading}
          isFetchingMore={isFetchingNextPage}
          hasMoreMessages={hasNextPage}
          onLoadMore={fetchNextPage}
          currentUserId={user?.id ? String(user.id) : undefined}
          onReaction={handleReaction}
          onRemoveReaction={handleRemoveReaction}
          onEdit={handleEditMessage}
          onDelete={handleDeleteMessage}
          onReply={handleReply}
        />

        {/* Typing Indicator - fixed height to prevent layout shift */}
        <Box sx={{ px: 2, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {currentTypingUsers.length > 0 && (
            <Typography level="body-xs" color="neutral" fontStyle="italic">
              {currentTypingUsers.length === 1
                ? `${currentTypingUsers[0].user_name} is typing...`
                : currentTypingUsers.length === 2
                  ? `${currentTypingUsers[0].user_name} and ${currentTypingUsers[1].user_name} are typing...`
                  : `${currentTypingUsers.length} people are typing...`}
            </Typography>
          )}
        </Box>

        <MessageComposer
          channelId={activeChannelId}
          placeholder={
            channel
              ? channel.channel_type === 'direct' && otherUser
                ? `Message ${otherUser.full_name || otherUser.username}`
                : `Message #${channel.name}`
              : 'Type a message...'
          }
          disabled={!isConnected}
          replyToMessageId={replyToMessageId}
          currentUserId={user?.id}
          onSend={handleSendMessage}
          onTyping={handleTyping}
          onCancelReply={() => setReplyToMessageId(null)}
        />
      </>
    );
  };

  return (
    <ChatLayout
      activeChannelId={activeChannelId}
      onChannelSelect={handleChannelSelect}
      onDMSelect={handleDMSelect}
    >
      {renderChatContent()}

      {/* Search Modal */}
      {activeChannelId && (
        <ChannelSearchModal
          open={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          channelId={activeChannelId}
          channelName={channel?.name}
        />
      )}

      {/* Thread View Drawer */}
      <ThreadView
        open={!!openThreadId}
        onClose={handleCloseThread}
        channelId={activeChannelId}
        parentMessageId={openThreadId}
        currentUserId={user?.id}
        onReaction={handleReaction}
        onRemoveReaction={handleRemoveReaction}
      />

      {/* Members Modal */}
      <Modal open={membersModalOpen} onClose={() => setMembersModalOpen(false)}>
        <ModalDialog sx={{ minWidth: 320, maxWidth: 420 }}>
          <ModalClose />
          <DialogTitle>Members</DialogTitle>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : channelMembers.length === 0 ? (
            <Typography level="body-sm" color="neutral" sx={{ py: 2 }}>
              No members found.
            </Typography>
          ) : (
            <List sx={{ maxHeight: 360, overflow: 'auto' }}>
              {channelMembers.map((member) => (
                <ListItem key={member.id}>
                  <ListItemDecorator>
                    <Avatar
                      size="sm"
                      src={member.user.avatar_url || undefined}
                      alt={member.user.full_name || member.user.username}
                    >
                      {(member.user.full_name || member.user.username || '?')
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>
                  </ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-sm">
                      {member.user.full_name || member.user.username}
                    </Typography>
                    {member.role_display && (
                      <Typography level="body-xs" color="neutral">
                        {member.role_display}
                      </Typography>
                    )}
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
          )}
        </ModalDialog>
      </Modal>

      {/* Invite Modal */}
      <Modal open={inviteModalOpen} onClose={() => setInviteModalOpen(false)}>
        <ModalDialog sx={{ minWidth: 320, maxWidth: 420 }}>
          <ModalClose />
          <DialogTitle>Invite people</DialogTitle>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : invitableUsers.length === 0 ? (
            <Typography level="body-sm" color="neutral" sx={{ py: 2 }}>
              Everyone is already in this channel.
            </Typography>
          ) : (
            <>
              <List sx={{ maxHeight: 320, overflow: 'auto' }}>
                {invitableUsers.map((u) => {
                  const userId = String(u.id);
                  const displayName =
                    u.full_name ||
                    `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
                    u.email;
                  return (
                    <ListItem key={userId}>
                      <ListItemButton onClick={() => handleToggleInvite(userId)}>
                        <ListItemDecorator>
                          <Checkbox checked={selectedInviteIds.has(userId)} readOnly />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography level="body-sm">{displayName}</Typography>
                          {u.email && (
                            <Typography level="body-xs" color="neutral">
                              {u.email}
                            </Typography>
                          )}
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={() => setInviteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitInvite}
                  disabled={selectedInviteIds.size === 0 || inviteToChannel.isPending}
                  loading={inviteToChannel.isPending}
                >
                  Invite{selectedInviteIds.size > 0 ? ` (${selectedInviteIds.size})` : ''}
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>
    </ChatLayout>
  );
};

export default ChatApp;
